import {createApi} from "@reduxjs/toolkit/query/react";
import {
  GetQuestionsResponseType,
  parseGetQuestionListResponse,
} from "../../features/problemset-table-v2/api/parseGetQuestionListResponse";
import {
  GetQuestionApiResponse,
  IQuestion,
  parseGetQuestionResponse,
} from "../../features/problem-dialog/api/parseGetQuestionResponse";
import {TableContentViewModel} from "../../components/table/TableUI";
import axiosBaseQuery from "../axiosBaseQuery";
import {
  GetSolvesInfoResponseType,
  ISolvesInfo,
  parseGetSolvesInfoResponse,
} from "../../features/pie-chart-analysis/api/parseGetSolvesInfoResponse";
import {StreakChartDataType} from "../../features/streak-chart";
import {
  GetStreakResponseType,
  parseGetStreakResponse,
} from "../../features/streak-chart/api/parseGetStreakResponse";

export const problemsetApiSlice = createApi({
  reducerPath: "problemsetApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    "User",
    "Exam",
    "Question",
    "QuestionList",
    "UserQuestion",
    "StreakGoal",
  ],
  endpoints: builder => ({
    /** Problemset APIs */
    getQuestionList: builder.query<
      Pick<TableContentViewModel, "rowsData" | "pagination">,
      {
        subjects: string[];
        chapters: string[];
        topics?: string[];
        attemptStatus: string[];
        patterns: string[];
        difficulty: string[];
        feature?: string;
        page: number;
      }
    >({
      query: params => ({
        url: `/core/questions/`,
        params: {
          subjects: params.subjects.join(","),
          chapters: params.chapters.join(","),
          topics: params.topics?.join(","),
          user_question_status: params.attemptStatus.join(","),
          question_types: params.patterns.join(","),
          difficulty_types: params.difficulty.join(","),
          page: params.page,
          ...(params.feature && {feature: params.feature}),
        },
      }),
      transformResponse: (response: GetQuestionsResponseType) => {
        return parseGetQuestionListResponse(response);
      },
      providesTags: ["User", "Exam", "QuestionList"],
    }),
    /** Question APIs */
    getQuestion: builder.query<IQuestion, {questionId: string}>({
      query: ({questionId}) => ({
        url: `/core/questions/${questionId}/`,
      }),
      transformResponse: (response: GetQuestionApiResponse) => {
        return parseGetQuestionResponse(response);
      },
      providesTags: ["User", "Exam", "Question"],
    }),
    updateQuestionResponse: builder.mutation<
      void,
      {questionId: string; answer: string}
    >({
      queryFn: async ({questionId, answer}, api) => {
        api.dispatch(
          problemsetApiSlice.util.updateQueryData(
            "getQuestion",
            {questionId},
            draft => {
              draft.userAnswer = answer;
            },
          ),
        );
        return {data: undefined};
      },
    }),
    submitQuestion: builder.mutation<
      void,
      {questionId: string; answer: string}
    >({
      query: ({questionId, answer}) => ({
        url: `/core/questions/${questionId}/submit/`,
        method: "POST",
        data: {answer},
      }),
      async onQueryStarted(_, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled;
          dispatch(problemsetApiSlice.util.invalidateTags(["UserQuestion"]));
        } catch {}
      },
      invalidatesTags: ["Question"],
    }),

    /** Analysis APIs */
    getSolvesInfo: builder.query<ISolvesInfo, void>({
      query: () => ({url: `/users/progress/`}),
      transformResponse: (response: GetSolvesInfoResponseType) => {
        return parseGetSolvesInfoResponse(response);
      },
      providesTags: ["User", "Exam", "UserQuestion"],
    }),
    updateStreakGoal: builder.mutation<void, {streakGoal: number}>({
      query: ({streakGoal}) => ({
        url: `/users/profile/`,
        method: "PATCH",
        data: {streak_goal: streakGoal},
      }),
      invalidatesTags: ["StreakGoal"],
    }),
    getStreak: builder.query<
      {
        streakGoal: number;
        data: StreakChartDataType[];
      },
      void
    >({
      query: () => ({url: `/users/streak/`}),
      transformResponse: (response: GetStreakResponseType) => {
        return parseGetStreakResponse(response);
      },
      providesTags: ["User", "Exam", "UserQuestion", "StreakGoal"],
    }),
  }),
});

export const {
  useGetQuestionListQuery,
  useGetQuestionQuery,
  useUpdateQuestionResponseMutation,
  useSubmitQuestionMutation,
  useGetSolvesInfoQuery,
  useUpdateStreakGoalMutation,
  useGetStreakQuery,
} = problemsetApiSlice;
